import { Check, Close, Delete, EditOutlined, NotificationsActive } from '@mui/icons-material';
import { Alert, Box, Button, Chip, IconButton, Stack, Switch, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { client } from '@frontend/features/common/services';
import { getStoredPushDeviceId } from '@frontend/features/common/services/pushSubscriptions';

import { pushSubscriptionQueryKey } from './query-key';

interface PushDeviceAgency {
  id: number;
  name: string;
}

interface PushDevice {
  deviceId: string;
  label: string | null;
  agencies: PushDeviceAgency[];
  suppressWhenActive: boolean;
  updatedAt: string;
}

interface PushTestResult {
  status: 'delivered' | 'expired' | 'failed';
}

export const PushDevicePreferences = ({ enabled }: { enabled: boolean }) => {
  const { t } = useTranslation();
  const { isAuthenticated, myProfile } = useAuth();
  const userId = myProfile?.id;
  const queryKey = pushSubscriptionQueryKey(userId ?? 'anonymous');
  const queryClient = useQueryClient();
  const currentDeviceId = getStoredPushDeviceId();
  const [editing, setEditing] = useState<{ deviceId: string; label: string }>();
  const [testResult, setTestResult] = useState<{ deviceId: string; status: PushTestResult['status'] }>();
  const { data = [] } = useQuery(
    queryKey,
    async (): Promise<PushDevice[]> => (await client.get('notifications/push/subscriptions')).data,
    { enabled: enabled && isAuthenticated && Boolean(userId) },
  );
  const update = useMutation(
    ({
      deviceId,
      ...changes
    }: Pick<PushDevice, 'deviceId'> & Partial<Pick<PushDevice, 'label' | 'suppressWhenActive'>>) =>
      client.patch(`notifications/push/subscriptions/${deviceId}`, changes),
    {
      onSuccess: (_response, variables) => {
        if (variables.label !== undefined) setEditing(undefined);
        queryClient.invalidateQueries(queryKey);
      },
    },
  );
  const test = useMutation(
    async (deviceId: string): Promise<PushTestResult> =>
      (await client.post<PushTestResult>(`notifications/push/subscriptions/${deviceId}/test`)).data,
    {
      onSuccess: ({ status }, deviceId) => setTestResult({ deviceId, status }),
      onError: (_error, deviceId) => setTestResult({ deviceId, status: 'failed' }),
    },
  );
  const revoke = useMutation((deviceId: string) => client.delete(`notifications/push/subscriptions/${deviceId}`), {
    onSuccess: () => queryClient.invalidateQueries(queryKey),
  });

  if (data.length === 0) return null;

  return (
    <Box mb={3}>
      <Typography variant="subtitle1" fontWeight={600}>
        {t('userNotificationTriggers.devices.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        {t('userNotificationTriggers.devices.help')}
      </Typography>
      <Stack gap={1.5} mt={1.5}>
        {data.map((device) => {
          const isCurrent = currentDeviceId === device.deviceId;
          const result = testResult?.deviceId === device.deviceId ? testResult.status : undefined;
          const agencyNames = device.agencies.map(({ name }) => name).join(', ');
          const isEditing = editing?.deviceId === device.deviceId;
          return (
            <Box key={device.deviceId} border={1} borderColor="divider" borderRadius={2} p={1.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-start' }} gap={1}>
                <Box minWidth={0} flex={1}>
                  {isEditing ? (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <TextField
                        fullWidth
                        size="small"
                        value={editing.label}
                        inputProps={{ maxLength: 100, 'aria-label': t('userNotificationTriggers.devices.label') }}
                        onChange={({ target }) => setEditing({ deviceId: device.deviceId, label: target.value })}
                        sx={{ '& .MuiInputBase-input': { fontSize: '16px' } }}
                      />
                      <IconButton
                        size="small"
                        disabled={!editing.label.trim() || update.isLoading}
                        aria-label={t('userNotificationTriggers.devices.save')}
                        onClick={() => update.mutate({ deviceId: device.deviceId, label: editing.label.trim() })}
                      >
                        <Check />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={t('userNotificationTriggers.devices.cancel')}
                        onClick={() => setEditing(undefined)}
                      >
                        <Close />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                      <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                        {device.label || t('userNotificationTriggers.devices.unnamed')}
                      </Typography>
                      {isCurrent && <Chip size="small" label={t('userNotificationTriggers.devices.current')} />}
                      <IconButton
                        size="small"
                        aria-label={t('userNotificationTriggers.devices.rename')}
                        onClick={() =>
                          setEditing({
                            deviceId: device.deviceId,
                            label: device.label || t('userNotificationTriggers.devices.unnamed'),
                          })
                        }
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {t('userNotificationTriggers.devices.coverage', {
                      agencies: agencyNames || t('userNotificationTriggers.devices.noAgencies'),
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('userNotificationTriggers.devices.lastRenewed', {
                      date: new Date(device.updatedAt).toLocaleString(),
                    })}
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
                  gap={0.5}
                >
                  <Button
                    size="small"
                    startIcon={<NotificationsActive />}
                    disabled={test.isLoading && test.variables === device.deviceId}
                    onClick={() => {
                      setTestResult(undefined);
                      test.mutate(device.deviceId);
                    }}
                  >
                    {t('userNotificationTriggers.devices.test')}
                  </Button>
                  <IconButton
                    onClick={() => revoke.mutate(device.deviceId)}
                    aria-label={t('userNotificationTriggers.devices.revoke')}
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1} gap={1}>
                <Typography variant="caption" color="text.secondary">
                  {t('userNotificationTriggers.devices.suppressWhenActive')}
                </Typography>
                <Switch
                  checked={device.suppressWhenActive}
                  onChange={(_event, checked) =>
                    update.mutate({ deviceId: device.deviceId, suppressWhenActive: checked })
                  }
                  inputProps={{ 'aria-label': t('userNotificationTriggers.devices.suppressWhenActive') }}
                />
              </Stack>
              {result && (
                <Alert severity={result === 'delivered' ? 'success' : 'error'} sx={{ mt: 1 }}>
                  {t(`userNotificationTriggers.devices.testResult.${result}`)}
                </Alert>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
